import React, { useState } from 'react';
import { Card, Badge, theme, Button } from 'antd';
import { PlusOutlined, EyeOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { getCatalogStatus } from '../../MyCatalogues/CatalogStatusConfig';
import { primaryShades, backgroundTokens, statusTokens } from '../../../theme/theme';

const { useToken } = theme;

// Costanti dei colori
const PRIMARY_200 = primaryShades.primary200;
const PRIMARY_500 = primaryShades.primary500;
const BG_PRIMARY = backgroundTokens.primaryBg;
const ERROR = statusTokens.error;
const BOX_SHADOW_COLOR = 'rgba(0, 0, 0, 0.08)';

/**
 * Componente che visualizza una card catalogo
 * @param {string} title - Titolo del catalogo
 * @param {number} assetCount - Numero di asset da visualizzare
 * @param {string} bgColor - Colore di sfondo
 * @param {number} badgeCount - Numero da visualizzare nel badge (opzionale)
 * @param {string} customText - Testo personalizzato per il pulsante quando non c'è title
 * @param {React.ReactNode} customIcon - Icona personalizzata per il pulsante quando non c'è title
 * @param {function} onGoToCatalogue - Callback per "Go to Catalogue"
 * @param {function} onGoToAssetsList - Callback per "Go to 3D assets list"
 * @param {Object} catalogue - Oggetto catalogo con informazioni di stato
 * @param {function} onClick - Callback per il click sulla card (quando non ha title)
 */
const CatalogueCard = ({
    title,
    assetCount,
    bgColor,
    badgeCount,
    customText,
    customIcon,
    onGoToCatalogue,
    onGoToAssetsList,
    catalogue,
    onClick
}) => {
    const { token } = useToken();
    const [showOverlay, setShowOverlay] = useState(false);

    const handleCardClick = (e) => {
        e.stopPropagation();
        if (title && onGoToCatalogue && onGoToAssetsList) {
            setShowOverlay(true);
        } else if (!title && onClick) {
            onClick();
        }
    };

    const handleOverlayClick = (e) => {
        e.stopPropagation();
        setShowOverlay(false);
    };

    const handleButtonClick = (callback) => (e) => {
        e.stopPropagation();
        setShowOverlay(false);
        if (callback) callback();
    };

    return (
        <div style={{ position: 'relative' }}>
            <Card
                style={{
                    borderRadius: 'clamp(0.25rem, 2vw, 0.5rem)',
                    overflow: 'hidden',
                    height: 'clamp(9rem, 20vh, 11.875rem)',
                    boxShadow: `0 clamp(0.0625rem, 0.5vw, 0.125rem) clamp(0.25rem, 2vw, 0.5rem) ${BOX_SHADOW_COLOR}`,
                    cursor: title ? 'pointer' : 'default'
                }}
                styles={{ body: { padding: 0, height: '100%' } }}
                onClick={handleCardClick}
            >
                {title ? (
                    <>
                        <div style={{
                            backgroundColor: PRIMARY_200,
                            padding: 'clamp(0.3125rem, 1.5vw, 0.5rem)',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            position: 'relative',
                            fontSize: 'clamp(0.75rem, 1.8vw, 0.8125rem)',
                            color: PRIMARY_500,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <span style={{ flex: 1 }}>{title}</span>

                            {/* Status Badge */}
                            {catalogue && (
                                <div style={{ position: 'absolute', right: 'clamp(0.3125rem, 1.5vw, 0.5rem)', top: '50%', transform: 'translateY(-50%)' }}>
                                    {(() => {
                                        const statusConfig = getCatalogStatus(catalogue);
                                        return (
                                            <div style={{
                                                backgroundColor: statusConfig.color,
                                                color: 'white',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                fontSize: 'clamp(0.5rem, 1.2vw, 0.625rem)',
                                                fontWeight: 'bold'
                                            }}>
                                                {statusConfig.label}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}


                        </div>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 'calc(100% - clamp(1.875rem, 6vw, 2.1875rem))',
                            backgroundColor: '#FFFFFF'
                        }}>
                            <div style={{ position: 'relative' }}>
                                <span style={{
                                    fontSize: 'clamp(2rem, 6vw, 3rem)',
                                    fontWeight: 'bold',
                                    color: PRIMARY_500,
                                    marginBottom: 'clamp(0.0625rem, 0.5vw, 0.125rem)',
                                    display: 'block',
                                    lineHeight: 1,
                                }}>
                                    {assetCount}
                                    {badgeCount && (
                                        <Badge
                                            count={badgeCount}
                                            style={{
                                                backgroundColor: ERROR,
                                                position: 'absolute',
                                                top: 'clamp(-2rem, -7vw, -2.5rem)',
                                                right: 'clamp(-0.75rem, -3vw, -0.9375rem)'
                                            }}
                                        />
                                    )}
                                </span>
                                <div style={{
                                    fontSize: 'clamp(0.6875rem, 1.5vw, 0.75rem)',
                                    textAlign: 'center',
                                    color: PRIMARY_500,
                                    fontWeight: 'bold'
                                }}>
                                    SKU(s)
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        cursor: 'pointer',
                        backgroundColor: '#FFFFFF'
                    }}>
                        {customIcon || <PlusOutlined style={{
                            fontSize: 'clamp(1.5rem, 5vw, 2rem)',
                            color: token.colorPrimary,
                            marginBottom: 'clamp(0.375rem, 2vw, 0.625rem)'
                        }} />}
                        <div style={{
                            color: token.colorPrimary,
                            fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                            fontWeight: 'bold',
                            marginTop: customIcon ? 'clamp(0.375rem, 2vw, 0.625rem)' : 0
                        }}>
                            {customText || "ADD CATALOGUE"}
                        </div>
                    </div>
                )}
            </Card>

            {/* Overlay con bottoni */}
            {showOverlay && title && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        borderRadius: 'clamp(0.25rem, 2vw, 0.5rem)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'clamp(0.5rem, 2vw, 1rem)',
                        zIndex: 10
                    }}
                    onClick={handleOverlayClick}
                >
                    <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        size="large"
                        style={{
                            borderRadius: '8px',
                            fontWeight: 'bold',
                            minWidth: '140px'
                        }}
                        onClick={handleButtonClick(onGoToCatalogue)}
                    >
                        Go to Catalogue
                    </Button>

                    <Button
                        type="default"
                        icon={<UnorderedListOutlined />}
                        size="large"
                        style={{
                            borderRadius: '8px',
                            fontWeight: 'bold',
                            minWidth: '140px',
                            backgroundColor: 'white',
                            borderColor: token.colorPrimary,
                            color: token.colorPrimary
                        }}
                        onClick={handleButtonClick(onGoToAssetsList)}
                    >
                        Go to 3D Assets
                    </Button>
                </div>
            )}
        </div>
    );
};

export default CatalogueCard; 